import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAutomationJobs, AutomationJob } from '@/hooks/useAutomationJobs';
import { Play, Pause, Square, Trash2, Download, Loader2, Bot, Eye, PackageOpen, RefreshCw, Clock, Zap, Rocket, FileText, PlayCircle, PauseCircle } from 'lucide-react';
import { AutomationJobImageGallery } from './AutomationJobImageGallery';
import { BulkPromptJobCreator } from './BulkPromptJobCreator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import JSZip from 'jszip';
import { useAvailableModels } from '@/hooks/useAvailableModels';
import { detectMimeType, getExtensionFromMime } from '@/utils/imageUtils';

const IMAGE_SIZES = [
  { value: '1024x1024', label: '1024x1024 (Square)' },
  { value: '1792x1024', label: '1792x1024 (Landscape)' },
  { value: '1024x1792', label: '1024x1792 (Portrait)' }
];

export const AdminAutomationAgent = () => {
  const { jobs, loading, processing, createJob, createBulkJobs, startJob, startAllPending, pauseJob, pauseAllRunning, stopJob, deleteJob, refreshJobs } = useAutomationJobs();
  const { models: availableModels, loading: modelsLoading } = useAvailableModels();
  const [jobName, setJobName] = useState('');
  const [basePrompt, setBasePrompt] = useState('');
  const [totalImages, setTotalImages] = useState('100');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [size, setSize] = useState('1024x1024');
  const [quality, setQuality] = useState('standard');
  const [style, setStyle] = useState('vivid');
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [viewJobId, setViewJobId] = useState<string | null>(null);
  const [viewJobName, setViewJobName] = useState<string>('');
  const [rememberSettings, setRememberSettings] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBulkCreator, setShowBulkCreator] = useState(false);

  // Format time since last refresh
  const getTimeSinceRefresh = () => {
    const seconds = Math.floor((new Date().getTime() - lastRefresh.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  // Update refresh timer every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(prev => prev); // Force re-render to update time display
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load saved settings on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('automation_job_settings');
    const rememberPref = localStorage.getItem('automation_remember_settings');
    
    if (rememberPref === 'true' && savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setBasePrompt(settings.basePrompt || '');
        setTotalImages(settings.totalImages || '100');
        setSelectedModels(settings.selectedModels || []);
        setSize(settings.size || '1024x1024');
        setQuality(settings.quality || 'standard');
        setStyle(settings.style || 'vivid');
        setRememberSettings(true);
      } catch (e) {
        console.error('Failed to load saved settings:', e);
      }
    }
  }, []);

  // Real-time subscription to job updates and image generation
  useEffect(() => {
    console.log('Setting up real-time subscriptions for automation jobs...');

    // Subscribe to job updates with unique channel name
    const jobsChannel = supabase
      .channel(`automation-jobs-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automated_generation_jobs'
        },
        (payload) => {
          console.log('Job update received:', payload);
          refreshJobs();
        }
      )
      .subscribe();

    // Subscribe to new images being generated with unique channel name
    const imagesChannel = supabase
      .channel(`generated-images-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_images'
        },
        (payload) => {
          console.log('New image generated:', payload);
          refreshJobs();
        }
      )
      .subscribe();

    // Poll every 10 seconds as backup and update last refresh time
    const interval = setInterval(() => {
      refreshJobs();
      setLastRefresh(new Date());
    }, 10000);

    return () => {
      console.log('Cleaning up real-time subscriptions...');
      supabase.removeChannel(jobsChannel);
      supabase.removeChannel(imagesChannel);
      clearInterval(interval);
    };
  }, []);


  const handleCreateJob = async () => {
    if (!jobName.trim()) {
      toast.error('Please enter a job name');
      return;
    }
    if (!basePrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Save settings if remember is enabled
    if (rememberSettings) {
      const settings = {
        basePrompt,
        totalImages,
        selectedModels,
        size,
        quality,
        style
      };
      localStorage.setItem('automation_job_settings', JSON.stringify(settings));
      localStorage.setItem('automation_remember_settings', 'true');
    } else {
      localStorage.removeItem('automation_job_settings');
      localStorage.removeItem('automation_remember_settings');
    }
    if (selectedModels.length === 0) {
      toast.error('Please select at least one model');
      return;
    }

    const target = parseInt(totalImages);
    if (isNaN(target) || target < 1) {
      toast.error('Please enter a valid number of images');
      return;
    }

    // Auto-increment job name if it already exists
    let finalJobName = jobName;
    const existingJobNames = jobs.map(j => j.job_name);
    
    if (existingJobNames.includes(finalJobName)) {
      // Find all jobs with the same base name
      const baseNamePattern = new RegExp(`^${finalJobName.replace(/\d+$/, '')}(\\d*)$`);
      const matchingJobs = existingJobNames.filter(name => baseNamePattern.test(name));
      
      // Extract numbers and find the highest
      const numbers = matchingJobs
        .map(name => {
          const match = name.match(/(\d+)$/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => !isNaN(num));
      
      const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
      finalJobName = `${jobName}${maxNumber + 1}`;
    }

    setCreatingJob(true);
    const job = await createJob({
      job_name: finalJobName,
      base_prompt: basePrompt,
      models_to_use: selectedModels,
      total_images_target: target,
      size,
      quality,
      style,
      rpm_limit: 5
    });

    if (job) {
      setJobName('');
      setBasePrompt('');
      // Only reset other settings if rememberSettings is false
      if (!rememberSettings) {
        setTotalImages('100');
        setSelectedModels([]);
        setSize('1024x1024');
        setQuality('standard');
        setStyle('vivid');
      }
    }
    setCreatingJob(false);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'pending': 'secondary',
      'running': 'default',
      'paused': 'outline',
      'completed': 'default',
      'failed': 'destructive',
      'stopped': 'outline'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const downloadJobImages = async (job: AutomationJob) => {
    const id = toast.loading('Preparing download...');
    try {
      // Fetch b64_json instead of image_url (which is just "base64" placeholder)
      const { data: images, error } = await supabase
        .from('generated_images')
        .select('id, b64_json, prompt')
        .eq('automated_job_id', job.id);

      if (error) throw error;

      if (!images || images.length === 0) {
        toast.error('No images found for this job', { id });
        return;
      }

      toast.loading(`Processing ${images.length} images...`, { id });

      const zip = new JSZip();
      const cleanJobName = job.job_name.replace(/[^a-zA-Z0-9_-]/g, '_');
      let successCount = 0;

      // Convert base64 to blob and add to zip
      for (let i = 0; i < images.length; i++) {
        try {
          const image = images[i];
          
          if (!image.b64_json || image.b64_json.length < 100) {
            console.warn(`Image ${i + 1} has no valid b64_json data`);
            continue;
          }

          // Detect MIME type from base64 data
          const mimeType = detectMimeType(image.b64_json);
          const ext = getExtensionFromMime(mimeType);

          // Convert base64 to blob
          const binaryString = atob(image.b64_json);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          const blob = new Blob([bytes], { type: mimeType });

          zip.file(`${cleanJobName}_${i + 1}.${ext}`, blob);
          successCount++;

          if ((i + 1) % 10 === 0) {
            toast.loading(`Processing ${i + 1}/${images.length} images...`, { id });
          }
        } catch (err) {
          console.error(`Failed to process image ${i + 1}:`, err);
        }
      }

      if (successCount === 0) {
        toast.error('No valid images could be processed', { id });
        return;
      }

      toast.loading('Generating zip file...', { id });

      // Generate zip file without compression to preserve image quality
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'STORE'
      });

      // Download zip file
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(zipBlob);
      link.href = objectUrl;
      link.download = `${cleanJobName}_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

      toast.success(`Downloaded ${successCount} images as zip file`, { id });
    } catch (error) {
      console.error('Error downloading images:', error);
      toast.error('Failed to download images', { id });
    }
  };

  const downloadAllImages = async () => {
    const id = toast.loading('Preparing to download all images...');
    try {
      // Fetch b64_json instead of image_url
      const { data: images, error } = await supabase
        .from('generated_images')
        .select('id, b64_json, automated_job_id')
        .not('automated_job_id', 'is', null);

      if (error) throw error;

      if (!images || images.length === 0) {
        toast.error('No images found', { id });
        return;
      }

      toast.loading(`Processing ${images.length} images...`, { id });

      const zip = new JSZip();
      let successCount = 0;

      // Convert base64 to blob and add to zip
      for (let i = 0; i < images.length; i++) {
        try {
          const image = images[i];
          
          if (!image.b64_json || image.b64_json.length < 100) {
            console.warn(`Image ${i + 1} has no valid b64_json data`);
            continue;
          }

          // Detect MIME type from base64 data
          const mimeType = detectMimeType(image.b64_json);
          const ext = getExtensionFromMime(mimeType);

          // Convert base64 to blob
          const binaryString = atob(image.b64_json);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          const blob = new Blob([bytes], { type: mimeType });

          zip.file(`automation_image_${i + 1}.${ext}`, blob);
          successCount++;

          if ((i + 1) % 10 === 0) {
            toast.loading(`Processing ${i + 1}/${images.length} images...`, { id });
          }
        } catch (err) {
          console.error(`Failed to process image ${i + 1}:`, err);
        }
      }

      if (successCount === 0) {
        toast.error('No valid images could be processed', { id });
        return;
      }

      toast.loading('Generating zip file...', { id });

      // Generate zip file without compression to preserve image quality
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'STORE'
      });

      // Download zip file
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(zipBlob);
      link.href = objectUrl;
      link.download = `automation_images_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

      toast.success(`Downloaded ${successCount} images as zip file`, { id });
    } catch (error) {
      console.error('Error downloading all images:', error);
      toast.error('Failed to download images', { id });
    }
  };

  // Combined refresh and sync function - merges both actions into one
  const handleRefreshAndSync = async () => {
    setIsRefreshing(true);
    try {
      // First refresh the jobs from database
      await refreshJobs();
      setLastRefresh(new Date());
      
      // If there are jobs, also sync counters automatically
      if (jobs.length > 0) {
        const { data, error } = await supabase.rpc('sync_automation_job_counters');
        if (error) {
          console.warn('Counter sync failed:', error);
        } else if (data && data.length > 0) {
          console.log('Synced counters for', data.length, 'jobs');
          // Refresh again to show updated counters
          await refreshJobs();
        }
      }
      
      toast.success('Refreshed and synced successfully');
    } catch (error) {
      console.error('Error refreshing:', error);
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  const deleteAllJobs = async () => {
    const id = toast.loading('Deleting all jobs...');
    try {
      for (const job of jobs) {
        await deleteJob(job.id);
      }
      toast.success('All jobs deleted successfully', { id });
      setDeleteAllConfirm(false);
    } catch (error) {
      console.error('Error deleting all jobs:', error);
      toast.error('Failed to delete all jobs', { id });
    }
  };

  // Manual process trigger - bypasses cron job issues
  const handleProcessNow = async () => {
    const runningJobs = jobs.filter(j => j.status === 'running');
    if (runningJobs.length === 0) {
      toast.error('No running jobs to process. Start a job first.');
      return;
    }

    setIsProcessing(true);
    const id = toast.loading('Processing automation jobs...');
    
    try {
      const { data, error } = await supabase.functions.invoke('process-automation-job', {
        body: {}
      });

      if (error) throw error;

      if (data?.results?.length > 0) {
        const totalGenerated = data.results.reduce((sum: number, r: any) => sum + (r.imagesGenerated || 0), 0);
        toast.success(`Generated ${totalGenerated} images across ${data.results.length} jobs`, { id });
      } else {
        toast.success('Processing complete', { id });
      }
      
      await refreshJobs();
      setLastRefresh(new Date());
    } catch (error: any) {
      console.error('Error processing jobs:', error);
      toast.error(error.message || 'Failed to process jobs', { id });
    } finally {
      setIsProcessing(false);
    }
  };

  const getProgressPercentage = (job: AutomationJob) => {
    const generated = Math.min(job.images_generated, job.total_images_target);
    return (generated / job.total_images_target) * 100;
  };

  const getETA = (job: AutomationJob) => {
    if (job.status !== 'running' || job.images_generated === 0) return 'N/A';
    
    const imagesRemaining = job.total_images_target - job.images_generated;
    const minutesRemaining = Math.ceil(imagesRemaining / job.rpm_limit);
    
    if (minutesRemaining < 60) {
      return `${minutesRemaining}m`;
    } else {
      const hours = Math.floor(minutesRemaining / 60);
      const minutes = minutesRemaining % 60;
      return `${hours}h ${minutes}m`;
    }
  };


  if (loading || modelsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.filter(j => j.status === 'running').length}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.reduce((sum, j) => sum + j.images_generated, 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const totalSuccess = jobs.reduce((sum, j) => sum + j.success_count, 0);
                const totalRequests = jobs.reduce((sum, j) => sum + j.success_count + j.error_count, 0);
                return totalRequests > 0 ? `${Math.round((totalSuccess / totalRequests) * 100)}%` : '0%';
              })()}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">RPM Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.filter(j => j.status === 'running').length * 5}/5
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create New Job */}
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardHeader>
          <CardTitle>Create Automation Job</CardTitle>
          <CardDescription>
            Set up a new image generation job that runs automatically
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Performance Info + Process Now Button */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-sm">
              <p className="text-blue-400 font-medium mb-1">⚡ Optimized Processing</p>
              <p className="text-muted-foreground">
                Jobs process 3 images at a time. Click "Process Now" to manually trigger processing.
              </p>
            </div>
            <Button
              onClick={handleProcessNow}
              disabled={isProcessing || jobs.filter(j => j.status === 'running').length === 0}
              className="bg-green-600 hover:bg-green-700 text-white h-auto py-3 px-6"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4 mr-2" />
              )}
              Process Now
            </Button>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="job-name">Job Name</Label>
              <Input
                id="job-name"
                placeholder="E.g., Product Mockups"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="total-images">Total Images</Label>
              <Input
                id="total-images"
                type="number"
                min="1"
                placeholder="100"
                value={totalImages}
                onChange={(e) => setTotalImages(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base-prompt">Base Prompt</Label>
            <Textarea
              id="base-prompt"
              placeholder="A professional product photo with white background..."
              value={basePrompt}
              onChange={(e) => setBasePrompt(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Image Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_SIZES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quality</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="hd">HD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vivid">Vivid</SelectItem>
                  <SelectItem value="natural">Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Models (select multiple)</Label>
            <div className="grid gap-2 md:grid-cols-2">
              {availableModels.map((model) => (
                <label
                  key={model.id}
                  className="flex items-center space-x-2 cursor-pointer rounded-md border p-2 hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={selectedModels.includes(model.model_key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedModels([...selectedModels, model.model_key]);
                      } else {
                        setSelectedModels(selectedModels.filter(m => m !== model.model_key));
                      }
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{model.display_name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
            <Checkbox
              id="rememberSettings"
              checked={rememberSettings}
              onCheckedChange={(checked) => setRememberSettings(checked === true)}
            />
            <Label htmlFor="rememberSettings" className="cursor-pointer text-sm">
              Remember these settings for next automation job
            </Label>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleCreateJob} disabled={creatingJob} className="flex-1">
              {creatingJob ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Job'
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkCreator(true)}
              className="border-primary/50 hover:bg-primary/10"
            >
              <FileText className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Jobs */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Jobs</h3>
            {jobs.filter(j => j.status === 'running').length > 0 && (
              <Badge variant="default" className="bg-green-500/20 text-green-400 border-green-500/30 animate-pulse">
                <Zap className="h-3 w-3 mr-1" />
                Processing
              </Badge>
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Updated {getTimeSinceRefresh()}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={handleRefreshAndSync}
              variant="outline"
              size="sm"
              disabled={isRefreshing}
              className="border-primary/50 hover:bg-primary/10"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {jobs.filter(j => j.status === 'pending').length > 0 && (
              <Button 
                onClick={startAllPending}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                Start All Pending ({jobs.filter(j => j.status === 'pending').length})
              </Button>
            )}
            {jobs.filter(j => j.status === 'running').length > 0 && (
              <Button 
                onClick={pauseAllRunning}
                variant="outline"
                size="sm"
              >
                <PauseCircle className="h-4 w-4 mr-1" />
                Pause All Running
              </Button>
            )}
            {jobs.some(job => job.images_generated > 0) && (
              <Button 
                onClick={downloadAllImages}
                size="sm"
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                <PackageOpen className="h-4 w-4 mr-2" />
                Download All
              </Button>
            )}
            {jobs.length > 0 && (
              <Button 
                onClick={() => setDeleteAllConfirm(true)}
                variant="destructive"
                size="sm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All
              </Button>
            )}
          </div>
        </div>
        {jobs.length === 0 ? (
          <Card className="bg-background/40 backdrop-blur-md border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <Bot className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No automation jobs yet. Create one above!</p>
            </CardContent>
          </Card>
        ) : (
          jobs.map((job) => (
            <Card key={job.id} className="bg-background/40 backdrop-blur-md border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{job.job_name}</CardTitle>
                    <CardDescription className="mt-1 line-clamp-1">
                      {job.base_prompt}
                    </CardDescription>
                  </div>
                  {getStatusBadge(job.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress: {Math.min(job.images_generated, job.total_images_target)} / {job.total_images_target}</span>
                    <span>ETA: {getETA(job)}</span>
                  </div>
                  <Progress value={getProgressPercentage(job)} className="h-2" />
                </div>

                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  <span>Models: {job.models_to_use.length}</span>
                  <span>•</span>
                  <span>Size: {job.size}</span>
                  <span>•</span>
                  <span>Quality: {job.quality}</span>
                  {job.status === 'running' && (
                    <>
                      <span>•</span>
                      <span className="text-green-400 flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        Active
                      </span>
                    </>
                  )}
                </div>

                {job.last_error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                    Error: {job.last_error}
                  </div>
                )}

                <div className="flex gap-2">
                  {job.status === 'pending' && (
                    <Button size="sm" onClick={() => startJob(job.id)}>
                      <Play className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  )}
                  {job.status === 'running' && (
                    <Button size="sm" variant="outline" onClick={() => pauseJob(job.id)}>
                      <Pause className="h-4 w-4 mr-1" />
                      Pause
                    </Button>
                  )}
                  {job.status === 'paused' && (
                    <Button size="sm" onClick={() => startJob(job.id)}>
                      <Play className="h-4 w-4 mr-1" />
                      Resume
                    </Button>
                  )}
                  {(job.status === 'running' || job.status === 'paused') && (
                    <Button size="sm" variant="destructive" onClick={() => stopJob(job.id)}>
                      <Square className="h-4 w-4 mr-1" />
                      Stop
                    </Button>
                  )}
                  {job.images_generated > 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setViewJobId(job.id);
                          setViewJobName(job.job_name);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View ({job.images_generated})
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadJobImages(job)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteJobId(job.id)}
                    className="ml-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent className="bg-background/40 backdrop-blur-md border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this automation job and all its associated images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteJobId) {
                  deleteJob(deleteJobId);
                  setDeleteJobId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={deleteAllConfirm} onOpenChange={setDeleteAllConfirm}>
        <AlertDialogContent className="bg-background/40 backdrop-blur-md border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Jobs?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {jobs.length} automation jobs and all their associated images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAllJobs}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Gallery Dialog */}
      {viewJobId && (
        <AutomationJobImageGallery
          jobId={viewJobId}
          jobName={viewJobName}
          open={!!viewJobId}
          onOpenChange={(open) => {
            if (!open) {
              setViewJobId(null);
              setViewJobName('');
            }
          }}
        />
      )}

      {/* Bulk Prompt Creator Dialog */}
      <BulkPromptJobCreator
        open={showBulkCreator}
        onOpenChange={setShowBulkCreator}
        availableModels={availableModels}
        onCreateJobs={async (prompts, settings) => {
          await createBulkJobs(prompts, settings);
        }}
      />
    </div>
  );
};
